// Types d'identifiants utilisateur — alignés sur Maincare et sur le lien OhMyVue.
// Les types CPS et CPE_CPA restent connus pour afficher les utilisateurs déjà enregistrés,
// mais ne sont plus proposés pour une nouvelle saisie (legacy: true).
const MATRICULE = (lib, n) =>
  `Carte CPE ou CPA : numéro ${lib} suivi d'une barre « / » et du matricule (première ligne de la carte, sans le préfixe ${n}).`;

export const USER_ID_TYPES = [
  // Carte CPS ou e-CPS
  { code: "RPPS", label: "Numéro RPPS", group: "Carte CPS ou e-CPS", prefix: "8",
    description: "11 chiffres, sur la première ligne de la carte CPS ou comme « Identifiant » dans l'application e-CPS (sans le 8 initial). Vérifié dans l'Annuaire santé.",
    placeholder: "10100123456", inputMode: "numeric", maxLength: 12, kind: "soignant" },
  // Carte CPE ou CPA
  { code: "FINESS", label: "Établissement FINESS", group: "Carte CPE ou CPA", prefix: "3",
    description: MATRICULE("FINESS (9 caractères)", 3),
    placeholder: "000000000/matricule", inputMode: "text", maxLength: 50, kind: "organisation" },
  { code: "SIREN", label: "Établissement SIREN", group: "Carte CPE ou CPA", prefix: "4",
    description: MATRICULE("SIREN (9 chiffres)", 4),
    placeholder: "000000000/matricule", inputMode: "text", maxLength: 50, kind: "organisation" },
  { code: "SIRET", label: "Établissement SIRET", group: "Carte CPE ou CPA", prefix: "5",
    description: MATRICULE("SIRET (14 chiffres)", 5),
    placeholder: "00000000000000/matricule", inputMode: "text", maxLength: 55, kind: "organisation" },
  { code: "ARMOIRE_RPPS", label: "Cabinet RPPS", group: "Carte CPE ou CPA", prefix: "6",
    description: MATRICULE("RPPS du praticien (11 chiffres)", 6),
    placeholder: "00000000000/matricule", inputMode: "text", maxLength: 52, kind: "organisation" },
  { code: "CABINET_ADELI", label: "Cabinet ADELI", group: "Carte CPE ou CPA", prefix: "1",
    description: MATRICULE("ADELI du praticien (9 chiffres)", 1),
    placeholder: "000000000/matricule", inputMode: "text", maxLength: 50, kind: "organisation" },
  // Étudiant
  { code: "ETUDIANT", label: "Étudiant", group: "Carte CPS étudiant ou e-CPS", prefix: "9",
    description: "11 chiffres, sur la première ligne de la carte CPS Étudiant ou comme « Identifiant » dans l'application e-CPS (sans le 9 initial).",
    placeholder: "00000000000", inputMode: "numeric", maxLength: 12, kind: "soignant" },
  // Autres
  { code: "ADELI", label: "ADELI", group: "Autres", prefix: "0",
    description: "Numéro ADELI à 9 chiffres.",
    placeholder: "000000000", inputMode: "numeric", maxLength: 9, kind: "soignant" },
  { code: "EMAIL", label: "Adresse e-mail (sans carte)", group: "Autres", prefix: "@",
    description: "Pour une personne sans carte CPS ni CPE : elle se connectera avec cette adresse.",
    placeholder: "prenom.nom@cabinet.fr", inputMode: "email", maxLength: 120, kind: "organisation" },
  // Anciens types (affichage uniquement)
  { code: "CPS", label: "Carte CPS / eCPS (ancien format)", group: "Anciens formats", legacy: true,
    description: "Ancien format : saisissez désormais le numéro RPPS de la carte.",
    placeholder: "", inputMode: "text", maxLength: 80, kind: "soignant" },
  { code: "CPE_CPA", label: "Carte CPE / CPA (ancien format)", group: "Anciens formats", legacy: true,
    description: "Ancien format : saisissez désormais l'identifiant établissement / matricule de la carte.",
    placeholder: "", inputMode: "text", maxLength: 80, kind: "soignant" },
];

const BY_CODE = Object.fromEntries(USER_ID_TYPES.map((t) => [t.code, t]));

export const getIdTypeMeta = (code) => BY_CODE[code] || null;

// Groupes d'options pour <select> (sans les anciens formats)
export const ID_TYPE_GROUPS = USER_ID_TYPES.filter((t) => !t.legacy).reduce((acc, t) => {
  acc[t.group] = acc[t.group] || [];
  acc[t.group].push(t);
  return acc;
}, {});

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
const FORMATS = {
  RPPS: [/^\d{11}$/, "11 chiffres (sans le 8 initial)."],
  ETUDIANT: [/^\d{11}$/, "11 chiffres (sans le 9 initial)."],
  ADELI: [/^\d{9}$/, "9 chiffres."],
  FINESS: [/^[0-9A-Za-z]{9}\/[^\s/]{1,40}$/, "FINESS / matricule, ex. 450000021/SEC01."],
  SIREN: [/^\d{9}\/[^\s/]{1,40}$/, "SIREN / matricule, ex. 325445203/ACC1."],
  SIRET: [/^\d{14}\/[^\s/]{1,40}$/, "SIRET / matricule, ex. 32544520300012/ACC1."],
  ARMOIRE_RPPS: [/^\d{11}\/[^\s/]{1,40}$/, "RPPS du praticien / matricule, ex. 10000000000/ASSIST1."],
  CABINET_ADELI: [/^\d{9}\/[^\s/]{1,40}$/, "ADELI du praticien / matricule, ex. 759999999/SEC01."],
  EMAIL: [EMAIL_RE, "Adresse e-mail invalide."],
};

// Retire le préfixe Maincare (8 ou 9) saisi par erreur
export function normaliserId(idType, raw) {
  const v = String(raw || "").replace(/\s/g, "");
  if ((idType === "RPPS" && /^8\d{11}$/.test(v)) || (idType === "ETUDIANT" && /^9\d{11}$/.test(v))) return v.slice(1);
  if (idType === "FINESS") return v.toUpperCase();
  if (idType === "EMAIL") return v.toLowerCase();
  return v;
}

export function validateUserId(idType, raw, { initial } = {}) {
  const t = BY_CODE[idType];
  if (!t) return { ok: false, error: "Type d'identifiant requis." };
  const v = normaliserId(idType, raw);
  if (!v) return { ok: false, error: "Identifiant requis." };
  // Un utilisateur déjà enregistré garde son identifiant tant qu'il n'est pas modifié
  if (initial && initial.id_type === idType && initial.professional_id === raw) return { ok: true };
  if (t.legacy) return { ok: false, error: `${t.description}` };
  const [re, msg] = FORMATS[idType] || [];
  if (re && !re.test(v)) return { ok: false, error: `Format attendu : ${msg}` };
  return { ok: true };
}

// Formate la saisie selon le type
export function formatIdInput(idType, raw) {
  const t = BY_CODE[idType];
  const v = raw || "";
  if (!t) return v;
  if (["RPPS", "ETUDIANT", "ADELI"].includes(idType)) return v.replace(/\D/g, "").slice(0, t.maxLength);
  if (idType === "EMAIL") return v.trim().slice(0, t.maxLength);
  return v.replace(/\s/g, "").slice(0, t.maxLength);
}

// Badge UX : Soignant vs Organisation
export function getUserBadge(idType) {
  const t = BY_CODE[idType];
  if (!t) return null;
  return t.kind === "soignant"
    ? { label: "Professionnel de santé", kind: "soignant" }
    : { label: "Utilisateur organisation", kind: "organisation" };
}

// Affichage condensé : "Numéro RPPS 10100123456"
export function formatUserIdLabel(idType, value) {
  if (!idType || !value) return "";
  const t = BY_CODE[idType];
  return `${t?.label || idType} ${value}`;
}

// Identifiant tel qu'il se saisit chez Maincare (préfixe + numéro)
export function idMaincare(idType, value) {
  const t = BY_CODE[idType];
  if (!t || !value) return value || "";
  if (!t.prefix || t.prefix === "@") return value;
  return `${t.prefix}${value}`;
}
